import { CheckCircle, Error, Warning } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'timeago.js';
import { Tooltip } from '~/components/Tooltip';
import { ago, compareInterval, formatDate } from '~/lib/datetime';
import { ValueWrapper } from '~/pages/devices/show/lib/styles';
import type { DeviceStatus__Device as Device } from './device-status.generated';

/* GraphQL */ `#graphql
  fragment DeviceStatus__Device on Device {
    activationChangedAt
    active
    id
    timeSinceUpdate {
      ...Interval
    }
  }
`;

export const StatusIcon = {
  success: <CheckCircle sx={{ color: 'success.main' }} />,
  error: <Error sx={{ color: 'error.main' }} />,
  warning: <Warning sx={{ color: 'warning.main' }} />,
};

export const activationStatus = (device: Device, t: (key: string) => string) => {
  const status = device.active ? 'success' : 'error';
  const title = device.active
    ? t('common.device_status.activated')
    : t('common.device_status.deactivated');
  const activationChanged = formatDate(device.activationChangedAt);
  const caption = `${title}${t('common.device_status.at')}${activationChanged as string}`;
  return { status, title, caption } as const;
};

export const connectionStatus = (
  { timeSinceUpdate }: Device,
  t: (key: string) => string,
  locale: string,
) => {
  if (timeSinceUpdate == null) {
    return {
      status: 'error' as const,
      title: t('common.device_status.never_connected'),
      caption: t('common.device_status.never_connected'),
    };
  }

  const result =
    compareInterval(timeSinceUpdate, { minutes: 10 }) > 0
      ? {
          status: 'error' as const,
          title: t('common.device_status.offline'),
        }
      : compareInterval(timeSinceUpdate, { minutes: 2 }) > 0
      ? {
          status: 'warning' as const,
          title: t('common.device_status.recently_connected'),
        }
      : {
          status: 'success' as const,
          title: t('common.device_status.online'),
        };
  return {
    ...result,
    caption: `${t('common.device_status.last_checked_in')}${format(ago(timeSinceUpdate), locale)}`,
  };
};

export interface DeviceStatusProps {
  device: Device;
  displayText?: boolean;
}

export const DeviceStatus = ({ device, displayText }: DeviceStatusProps) => {
  const { t, i18n } = useTranslation();
  const { caption, status, title } = device.active
    ? connectionStatus(device, t, i18n.language)
    : activationStatus(device, t);
  return (
    <Tooltip title={caption}>
      <ValueWrapper>
        {/* Icon only, the icon carries the status name; with text it is decorative. */}
        {displayText ? (
          StatusIcon[status]
        ) : (
          <Box component="span" role="img" aria-label={title} sx={{ display: 'flex' }}>
            {StatusIcon[status]}
          </Box>
        )}
        {displayText && (
          <Typography variant="h6" sx={{ color: `${status}.text` }}>
            {title}
          </Typography>
        )}
      </ValueWrapper>
    </Tooltip>
  );
};
